import React, { useState } from "react";
import { Search, Inbox } from "lucide-react";
import { Input } from "../ui/input";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { EmptyState } from "../common";
import { CardListSkeleton, PaginationBar, useDebounced, usePagedList } from "../PaginationBar";
import { errMsg, fmtDateTime, fmtINR } from "../../lib/api";
import { SafEscalationTags, SafStatusBadge, stageDeadlineText } from "./SafStatus";

/** Compact list row used by the collector / supervisor / admin queues. */
export function SafListRow({ saf, onOpen, testId }) {
  const clock = stageDeadlineText(saf);
  return (
    <button type="button" onClick={onOpen} className="w-full text-left border rounded-lg px-3 py-2.5 bg-card hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
      data-testid={testId} data-status={saf.status} data-escalated={saf.auto_escalated ? "true" : "false"}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-mono font-medium">{saf.saf_id}{saf.pin ? <span className="font-sans text-emerald-700 text-xs ml-2">PIN {saf.pin}</span> : null}</p>
          <p className="text-sm truncate">{saf.owner_name}{saf.house_no ? ` · ${saf.house_no}` : ""}</p>
          <p className="text-xs text-muted-foreground truncate">{[saf.zone_name, saf.ward_name, saf.mohalla_name].filter(Boolean).join(" › ")}{saf.address ? ` · ${saf.address}` : ""}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {saf.assigned_collector_name ? `Collector: ${saf.assigned_collector_name}` : (saf.skipped_collector ? "Verification skipped (no collector mapped)" : "No collector assigned")} · Submitted {fmtDateTime(saf.submitted_at)}
            {saf.citizen_remarks ? <span className="text-amber-700 font-medium"> · Concern raised</span> : null}
          </p>
          <SafEscalationTags tags={saf.escalation_tags} className="mt-1" testId={`${testId}-tags`} />
          {clock && <p className={`text-[11px] mt-1 ${clock.overdue ? "text-rose-700 font-medium" : "text-muted-foreground"}`} data-testid={`${testId}-clock`}>{clock.text}</p>}
        </div>
        <div className="text-right shrink-0 space-y-1">
          <SafStatusBadge status={saf.status} />
          <p className="text-xs tabular-nums text-muted-foreground">{(saf.final_lines || []).length ? `Final ${fmtINR(saf.final_totals?.total || 0)}` : `Est. ${fmtINR(saf.estimate_total || 0)}`}</p>
        </div>
      </div>
    </button>
  );
}

/**
 * Phase 207 — role-scoped SAF queue (the API already scopes: collector = assigned to me, supervisor = my wards, admin = all).
 *  tab "pending" = waiting for THIS role's action · "all" = every open + closed case in scope · "closed" = approved / rejected.
 */
const TAB_LABELS = { pending: "Pending for me", all: "All", closed: "Closed", escalated: "Auto-escalated" };

export function SafQueue({ code, onOpen, reloadKey = 0, testIdPrefix = "saf-queue", initialTab = "pending", pendingLabel, limit = 10, compact = false, showEscalated = false }) {
  const [tab, setTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 350);
  const list = usePagedList(`/t/${code}/saf`, { params: { tab, search: q || undefined }, initialLimit: limit, reloadKey });
  const counts = list.data?.counts || {};
  const pending = counts.pending_for_me;
  const escalated = counts.escalated_open ?? counts.escalated;
  const tabs = showEscalated ? ["pending", "escalated", "all", "closed"] : ["pending", "all", "closed"];

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
          <TabsList className="w-full sm:w-auto">
            {tabs.map((t) => (
              <TabsTrigger key={t} value={t} className="flex-1 sm:flex-none" data-testid={`${testIdPrefix}-tab-${t}`}>
                {t === "pending" ? (pendingLabel || TAB_LABELS.pending) : TAB_LABELS[t]}
                {t === "pending" && pending != null ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-primary/10 text-primary text-[11px] px-1.5 tabular-nums" data-testid={`${testIdPrefix}-pending-count`}>{pending}</span> : null}
                {t === "escalated" && escalated != null ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-rose-100 text-rose-700 text-[11px] px-1.5 tabular-nums" data-testid={`${testIdPrefix}-escalated-count`}>{escalated}</span> : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative sm:ml-auto w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9 h-10" placeholder="SAF no / owner / PIN / mobile / address" value={search} aria-label="Search self-assessments"
            onChange={(e) => setSearch(e.target.value)} data-testid={`${testIdPrefix}-search`} />
        </div>
      </div>

      {list.error ? (
        <EmptyState title="Could not load self-assessments" subtitle={errMsg(list.error)}
          action={<Button variant="outline" onClick={list.reload} data-testid={`${testIdPrefix}-retry`}>Try again</Button>} />
      ) : list.loading && !list.data ? (
        <CardListSkeleton rows={compact ? 3 : 5} testId={`${testIdPrefix}-skeleton`} />
      ) : list.items.length === 0 ? (
        <div className="border border-dashed rounded-xl p-6 text-center" data-testid={`${testIdPrefix}-empty`}>
          <div className="h-10 w-10 rounded-xl bg-accent mx-auto flex items-center justify-center mb-2"><Inbox className="h-5 w-5 text-accent-foreground" /></div>
          <p className="text-sm font-medium">{tab === "pending" ? "Nothing waiting for your action" : tab === "escalated" ? "No auto-escalated self-assessments" : q ? "No self-assessment matches this search" : "No self-assessments here yet"}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {tab === "pending" ? "New citizen self-assessments appear here as soon as they reach your stage."
              : tab === "escalated" ? "Cases that were escalated automatically because the Field Collector or Supervisor did not act within the configured time limit will be listed here."
              : "Citizen self-assessments in your scope will be listed here."}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
          {list.items.map((s) => <SafListRow key={s.id} saf={s} onOpen={() => onOpen(s)} testId={`${testIdPrefix}-row-${s.saf_id}`} />)}
        </div>
      )}
      {list.data && list.total > limit && (
        <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
          onPageChange={list.setPage} onLimitChange={list.setLimit} sizes={[10, 25, 50]} testId={`${testIdPrefix}-pagination`} />
      )}
    </div>
  );
}
